import normalizeDateString from "../common/normalizeDateString.js";
import {
  baseLabels,
  buildMoodSynonyms,
  buildMoodGenitiveLabels,
} from "../../config/moodImagePaths.js";

function seededRandom(seed) {
  let value = seed;
  return function () {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
}

const DEFAULT_THEME = "animals";
const DEFAULT_COLLECTION = "rainforest";

export default function getMoodImages(theme, collection, t, selectedDate) {
  const themeKey = (theme && String(theme).trim()) || DEFAULT_THEME;
  const collectionKey = DEFAULT_COLLECTION; // TODO (collection && String(collection).trim()) ||
  const normalizedDate =
    normalizeDateString(selectedDate) || normalizeDateString(new Date());
  const dateSuffix = normalizedDate ? `-${normalizedDate}` : "";
  const moodSynonyms = buildMoodSynonyms(t);

  const CACHE_VERSION = "v2";
  const storageKey = `moodImageOrder${dateSuffix}-${CACHE_VERSION}`;
  const labelKey = `moodLabelVariants${dateSuffix}`;
  const packageKey = `moodImagePackage${dateSuffix}`;

  const imagePackages = [
    {
      images: [
        `${themeKey.toLowerCase()}/${collectionKey}/apathic.png`,
        `${themeKey.toLowerCase()}/${collectionKey}/anxious.png`,
        `${themeKey.toLowerCase()}/${collectionKey}/calm.png`,
        `${themeKey.toLowerCase()}/${collectionKey}/flow.png`,
      ],
    },
  ];

  const dateHash = normalizedDate.split("").reduce((acc, char) => {
    return (acc << 5) - acc + char.charCodeAt(0);
  }, 0);
  const random = seededRandom(Math.abs(dateHash));

  const getPackageForDate = (date) => {
    const normalized = normalizeDateString(date);
    if (!normalized) return null;
    const hash = normalized.split("").reduce((acc, char) => {
      return (acc << 5) - acc + char.charCodeAt(0);
    }, 0);
    const dateRandom = seededRandom(Math.abs(hash));
    const packageIndex = Math.floor(dateRandom() * imagePackages.length);
    return imagePackages[packageIndex].name;
  };

  let selectedImages;
  const cachedPackage = sessionStorage.getItem(packageKey);
  let selectedPackageName = cachedPackage;

  if (
    !selectedPackageName ||
    !imagePackages.find((pkg) => pkg.name === selectedPackageName)
  ) {
    const packageIndex = Math.floor(random() * imagePackages.length);
    const selectedPackage = imagePackages[packageIndex];
    selectedPackageName = selectedPackage.name;
    selectedImages = selectedPackage.images;

    sessionStorage.setItem(packageKey, selectedPackageName);
  } else {
    const selectedPackage = imagePackages.find(
      (pkg) => pkg.name === selectedPackageName
    );
    selectedImages = selectedPackage
      ? selectedPackage.images
      : imagePackages[0].images;
  }

  // Debug: console log statements for package selections
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfterTomorrow = new Date(today);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);

  console.log("Package selections:", {
    yesterday: getPackageForDate(yesterday),
    today: getPackageForDate(today),
    tomorrow: getPackageForDate(tomorrow),
    dayAfterTomorrow: getPackageForDate(dayAfterTomorrow),
  });

  // Generate mood label variants
  let labelVariants;
  const cachedLabels = sessionStorage.getItem(labelKey);
  if (cachedLabels) {
    try {
      labelVariants = JSON.parse(cachedLabels);
    } catch {}
  }

  if (!labelVariants) {
    labelVariants = baseLabels.map((base) => {
      const synonyms = moodSynonyms[base] || [base];
      // Use seeded random for deterministic label selection per date
      const rand = Math.floor(random() * synonyms.length);
      return synonyms[rand];
    });
    sessionStorage.setItem(labelKey, JSON.stringify(labelVariants));
  }

  // Pair images with randomized mood labels
  const moodGenitiveLabels = buildMoodGenitiveLabels(t);

  const paired = selectedImages.map((src, index) => ({
    src,
    label: labelVariants[index] || "Mood",
    enLabel: baseLabels[index],
    moodGenitive: moodGenitiveLabels[index] || "Mood",
  }));

  // Check sessionStorage for existing shuffle
  const cached = sessionStorage.getItem(storageKey);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length === paired.length) {
        return parsed;
      }
    } catch {}
  }

  // Shuffle the array using seeded random (Fisher–Yates)
  for (let i = paired.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [paired[i], paired[j]] = [paired[j], paired[i]];
  }

  sessionStorage.setItem(storageKey, JSON.stringify(paired));

  return paired;
}
